import {
    approveStn, cancelStn, dispatchStn,
    listStns, listWarehousesForStn, type Stn, type WarehousePickItem,
} from '@api/warehouse-ops';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Group } from '@mantine/core';
import { Plus } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router';

import CreateStnDrawer from './CreateStnDrawer';
import StnDetailPanel from './StnDetailPanel';
import StnStatusBadge from './StnStatusBadge';
import { useStnReceivePath } from './stnReceivePath';

const PAGE_SIZE = 25;

const OutboundContainer: FC = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [selectedStnId, setSelectedStnId] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // A transfer's GRN opens where its stock lands: a store's RM/PM GRN, a site, or the FG form.
    const { pathFor: receivePathFor, ready: receiveReady } = useStnReceivePath();

    const query = useQuery({
        queryKey: ['stns', page],
        queryFn: () => listStns({ page, take: PAGE_SIZE }),
    });

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['warehouses-picker-stn'],
        queryFn: () => listWarehousesForStn(),
        staleTime: Infinity,
    });
    const warehouseNameMap = useMemo(() =>
        new Map((warehousesQuery.data ?? []).map((w) => [w.warehouseId, w.name])),
        [warehousesQuery.data],
    );

    const approveMutation = useMutation({
        mutationFn: (id: string) => approveStn(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stns'] }),
    });
    const dispatchMutation = useMutation({
        mutationFn: (id: string) => dispatchStn(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stns'] }),
    });
    const cancelMutation = useMutation({
        mutationFn: (id: string) => cancelStn(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stns'] }),
    });

    const data = query.data?.data ?? [];
    const meta = query.data?.meta;

    const columns: DataSheetColumn<Stn>[] = [
        {
            key: 'code', header: 'STN ID', width: 140, sticky: true,
            render: (_, r) => <span className="font-medium">{r.code}</span>,
        },
        {
            key: 'fromNodeId', header: 'From', width: 200,
            render: (_, r) => warehouseNameMap.get(r.fromNodeId) ?? r.fromNodeId,
        },
        {
            key: 'toNodeId', header: 'To', width: 200,
            render: (_, r) => warehouseNameMap.get(r.toNodeId) ?? r.toNodeId,
        },
        {
            key: 'itemType', header: 'Type', width: 130,
            render: (_, r) => r.itemType === 'product' ? 'Finished Good' : 'Raw / PM',
        },
        {
            key: 'status', header: 'Status', width: 120,
            render: (_, r) => <StnStatusBadge status={r.status} />,
        },
        { key: 'createdAt', header: 'Date', width: 110, render: (_, r) => new Date(r.createdAt).toLocaleDateString() },
        {
            key: '_actions', header: '', width: 220,
            render: (_, r) => {
                // Only where a GRN lands stock: a site, a store, or one of our warehouses.
                const receivePath = receiveReady ? receivePathFor(r) : null;
                return (
                <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                    {r.status === 'DRAFT' && (
                        <>
                            <Button size="xs" variant="light" color="violet"
                                loading={approveMutation.isPending}
                                onClick={() => approveMutation.mutate(r.id)}>
                                Approve
                            </Button>
                            <Button size="xs" variant="subtle" color="red"
                                loading={cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate(r.id)}>
                                Cancel
                            </Button>
                        </>
                    )}
                    {r.status === 'APPROVED' && (
                        <Button size="xs" variant="light" color="blue"
                            loading={dispatchMutation.isPending}
                            onClick={() => dispatchMutation.mutate(r.id)}>
                            Dispatch
                        </Button>
                    )}
                    {(r.status === 'DISPATCHED' || r.status === 'IN_TRANSIT') && receivePath && (
                        <Button size="xs" variant="light" color="green"
                            onClick={() => navigate(receivePath)}>
                            Create GRN
                        </Button>
                    )}
                </Group>
                );
            },
        },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="📤"
                    title="Outbound"
                    subtitle="Create and manage stock transfer notes between locations"
                />
                <Button
                    leftSection={<Plus size={14} />}
                    onClick={() => setDrawerOpen(true)}
                >
                    Create STN
                </Button>
            </Group>

            <DataSheet<Stn>
                className="flex-1 min-h-0"
                data={data}
                columns={columns}
                isLoading={query.isLoading}
                getRowKey={(r) => r.id}
                onRowClick={(r) => setSelectedStnId(r.id)}
                footer={
                    meta && meta.pageCount > 1 ? (
                        <Group justify="center" p="sm">
                            <Button size="xs" variant="subtle" disabled={!meta.hasPreviousPage} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                            <span style={{ fontSize: 12 }}>Page {meta.page} of {meta.pageCount}</span>
                            <Button size="xs" variant="subtle" disabled={!meta.hasNextPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
                        </Group>
                    ) : undefined
                }
            />

            <CreateStnDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} />
            {selectedStnId && (
                <StnDetailPanel stnId={selectedStnId} onClose={() => setSelectedStnId(null)} />
            )}
        </div>
    );
};

export default OutboundContainer;
